import React, { useState } from "react";
import Markdown from "../Markdown/Markdown";
import { lpTrack } from "../../helpers/lpTrack";

/**
 * Paid-LP FAQ accordion — NEW page-scoped component (the shared Accordion stays
 * untouched). Differences requested in the redesign brief:
 *  - +/× toggle icon (a "+" that rotates 45° to "×"), pure CSS.
 *  - 2 columns on desktop, 1 on mobile.
 *  - Proper a11y: button[aria-expanded] + aria-controls -> panel[role=region].
 *  - Single item open at a time. faq_open analytics on expand.
 * Consumes the same `component.accordion` block data; answers render via the
 * shared Markdown component (so tel: links work).
 */

interface AccordionItemData {
  Title: string;
  Text: string;
  TitleType?: string;
}
interface AccordionPaidProps {
  data: {
    Title?: string;
    Items: AccordionItemData[];
  };
}

const ToggleIcon = ({ open }: { open: boolean }) => (
  <span
    aria-hidden="true"
    className={`relative inline-block h-[20px] w-[20px] shrink-0 transition-transform duration-200 ${
      open ? "rotate-45" : ""
    }`}
  >
    <span className="absolute top-1/2 left-0 h-[2px] w-full -translate-y-1/2 bg-[var(--primary-color)]" />
    <span className="absolute left-1/2 top-0 h-full w-[2px] -translate-x-1/2 bg-[var(--primary-color)]" />
  </span>
);

const Item = ({
  item,
  index,
  open,
  onToggle,
}: {
  item: AccordionItemData;
  index: number;
  open: boolean;
  onToggle: (i: number) => void;
}) => {
  const panelId = `faq-panel-${index}`;
  const btnId = `faq-button-${index}`;
  return (
    <div className="border-b border-neutral-200">
      <button
        type="button"
        id={btnId}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => onToggle(index)}
        className="flex w-full items-center justify-between gap-[16px] py-[20px] text-left"
      >
        <span className="font-DM font-bold text-[#424242] text-[17px] md:text-[18px] leading-[1.4]">
          {item.Title}
        </span>
        <ToggleIcon open={open} />
      </button>
      <div
        id={panelId}
        role="region"
        aria-labelledby={btnId}
        className={`grid transition-[grid-template-rows] duration-300 ease-out ${
          open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        }`}
      >
        <div className="overflow-hidden">
          <div className="pb-[20px] text-[16px] leading-[26px] text-placeholder">
            <Markdown>{item.Text}</Markdown>
          </div>
        </div>
      </div>
    </div>
  );
};

const AccordionPaid = ({ data }: AccordionPaidProps) => {
  const [openIndex, setOpenIndex] = useState<number>(-1);
  const items = data?.Items || [];

  const onToggle = (i: number) => {
    const willOpen = openIndex !== i;
    setOpenIndex(willOpen ? i : -1);
    if (willOpen) lpTrack("faq_open", { question: items[i]?.Title });
  };

  const half = Math.ceil(items.length / 2);
  const columns = [items.slice(0, half), items.slice(half)];

  return (
    <section className="bg-white">
      <div className="max-w-content mx-auto px-[24px] py-[48px] md:py-[64px]">
        {data?.Title ? (
          <h2 className="font-DM font-bold text-[var(--primary-color)] text-[28px] md:text-[32px] leading-tight mb-[24px] md:mb-[32px]">
            {data.Title}
          </h2>
        ) : null}
        <div className="grid grid-cols-1 md:grid-cols-2 md:gap-x-[48px]">
          {columns.map((col, c) => (
            <div key={c}>
              {col.map((item, i) => {
                const index = c === 0 ? i : half + i;
                return (
                  <Item
                    key={index}
                    item={item}
                    index={index}
                    open={openIndex === index}
                    onToggle={onToggle}
                  />
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default AccordionPaid;
